import { Auth0Provider, useAuth0, type AppState } from '@auth0/auth0-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSignOut } from '@/hooks/use-sign-out';
import { revocableTokenCache } from '@/lib/auth-token-cache';
import { RUNTIME_CONFIG } from '@/lib/runtime-config';

const AUTH0_DOMAIN = RUNTIME_CONFIG.auth0Domain;
const AUTH0_CLIENT_ID = RUNTIME_CONFIG.auth0ClientId;
const AUTH0_AUDIENCE = RUNTIME_CONFIG.auth0Audience;

export const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
export const WARNING_BEFORE_TIMEOUT_MS = 60 * 1000;
export const ACTIVITY_THROTTLE_MS = 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;

interface AuthProviderProps {
    children: ReactNode;
}

export function InactivityWatcher() {
    const { isAuthenticated } = useAuth0();
    const signOut = useSignOut();
    const [warningOpen, setWarningOpen] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(Math.ceil(WARNING_BEFORE_TIMEOUT_MS / 1000));

    const warnTimer = useRef<number | undefined>(undefined);
    const logoutTimer = useRef<number | undefined>(undefined);
    const countdownInterval = useRef<number | undefined>(undefined);
    // Mirrors warningOpen so the activity listener can read it without re-binding on every change.
    const warningOpenRef = useRef(false);
    const lastActivityResetRef = useRef(0);

    const clearAll = useCallback(() => {
        if (warnTimer.current !== undefined) window.clearTimeout(warnTimer.current);
        if (logoutTimer.current !== undefined) window.clearTimeout(logoutTimer.current);
        if (countdownInterval.current !== undefined) window.clearInterval(countdownInterval.current);
        warnTimer.current = undefined;
        logoutTimer.current = undefined;
        countdownInterval.current = undefined;
    }, []);

    const doLogout = useCallback(() => {
        clearAll();
        warningOpenRef.current = false;
        setWarningOpen(false);
        signOut();
    }, [clearAll, signOut]);

    const startTimers = useCallback(() => {
        clearAll();
        warningOpenRef.current = false;
        setWarningOpen(false);
        lastActivityResetRef.current = Date.now();

        warnTimer.current = window.setTimeout(() => {
            const logoutAt = Date.now() + WARNING_BEFORE_TIMEOUT_MS;
            warningOpenRef.current = true;
            setWarningOpen(true);
            setSecondsLeft(Math.ceil(WARNING_BEFORE_TIMEOUT_MS / 1000));
            countdownInterval.current = window.setInterval(() => {
                setSecondsLeft(Math.max(0, Math.ceil((logoutAt - Date.now()) / 1000)));
            }, 1000);
            logoutTimer.current = window.setTimeout(doLogout, WARNING_BEFORE_TIMEOUT_MS);
        }, INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_TIMEOUT_MS);
    }, [clearAll, doLogout]);

    useEffect(() => {
        if (!isAuthenticated) return;

        const onActivity = () => {
            if (warningOpenRef.current) return;
            if (Date.now() - lastActivityResetRef.current < ACTIVITY_THROTTLE_MS) return;
            startTimers();
        };

        ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, onActivity, { passive: true }));
        startTimers();

        return () => {
            clearAll();
            ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onActivity));
        };
    }, [isAuthenticated, startTimers, clearAll]);

    return (
        <AlertDialog open={warningOpen}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Still there?</AlertDialogTitle>
                    <AlertDialogDescription>
                        {`You will be signed out in ${secondsLeft} second${secondsLeft === 1 ? '' : 's'} due to a period of inactivity.`}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={startTimers}>Stay signed in</AlertDialogCancel>
                    <AlertDialogAction onClick={doLogout}>Log out now</AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

export function AuthProvider({ children }: AuthProviderProps) {
    const redirectUri = `${window.location.origin}`;

    const handleRedirectCallback = (appState?: AppState) => {
        // Navigate to the returnTo URL if provided, otherwise default to root
        const returnTo = appState?.returnTo || '/';
        // Use location.replace to navigate and trigger React Router
        window.location.replace(returnTo);
    };

    return (
        <Auth0Provider
            domain={AUTH0_DOMAIN}
            clientId={AUTH0_CLIENT_ID}
            authorizationParams={{
                redirect_uri: redirectUri,
                audience: AUTH0_AUDIENCE,
                scope: 'openid profile email',
            }}
            // Wraps the SDK's own localStorage cache — same storage, but the
            // refresh token can be revoked at sign-out (ESCLOUD-112). Takes
            // precedence over `cacheLocation`, which is why that is now absent.
            cache={revocableTokenCache}
            useRefreshTokens={true}
            useRefreshTokensFallback={true}
            onRedirectCallback={handleRedirectCallback}
        >
            <InactivityWatcher />
            {children}
        </Auth0Provider>
    );
}

export { useAuth0 };
